import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Typography,
  Box,
  TextField,
  MenuItem,
  Pagination,
  Skeleton,
  Chip,
  FormControl,
  Select,
  Slider,
  Button,
  Drawer,
  IconButton,
  FormControlLabel,
  Checkbox,
  Collapse,
  Link,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ViewSidebar as ViewSidebarIcon,
} from '@mui/icons-material';
import ProductCard from '../components/ProductCard';
import { productsApi } from '../api/products';
import { getTemplateStyle } from '../theme/templateStyles';
import Seo from '../components/Seo';

const ProductsWithDynamicFilters = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Merchant-configured desktop column count (2-5); breakpoints step up from it
  const gridColumns = useSelector((state) => state.template?.layout?.productGridColumns) || 4;
  const gridItemWidth = (count) => 12 / Math.min(6, Math.max(2, count));
  // 'full' listing templates land on an uncluttered full-width grid (filters stay a click away)
  const templateName = useSelector((state) => state.template?.template);
  const fullWidthListing = getTemplateStyle(templateName).listing === 'full';
  const queryParams = new URLSearchParams(location.search);
  const categorySlug = queryParams.get('category') || queryParams.get('categoryName'); // Support both parameter names
  const subcategorySlug = queryParams.get('subcategory');
  const searchQuery = queryParams.get('search') || queryParams.get('q') || ''; // Support both 'search' and 'q' parameters
  const teamQuery = queryParams.get('team');
  // State management
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [team, setTeam] = useState(teamQuery || '');
  const [sortBy, setSortBy] = useState('featured');
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [desktopFiltersOpen, setDesktopFiltersOpen] = useState(!fullWidthListing);
  const itemsPerPage = 20;

  // Dynamic filter data from API
  const [businessVariations, setBusinessVariations] = useState([]);
  const [businessTags, setBusinessTags] = useState([]);
  const [priceMinMax, setPriceMinMax] = useState({ minPrice: 0, maxPrice: 10000 });

  // Filter states
  const [selectedTags, setSelectedTags] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [tempPriceRange, setTempPriceRange] = useState([0, 10000]); // Temporary range for slider
  const [selectedVariations, setSelectedVariations] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    tags: false,
    price: false,
    availability: false,
  });

  // Color mapping
  const colorHexMap = {
    'RED': '#DC2626',
    'BLUE': '#2563EB',
    'WHITE': '#F3F4F6',
    'BLACK': '#000000',
    'YELLOW': '#FCD34D',
    'GREEN': '#10B981',
    'ORANGE': '#FB923C',
    'PURPLE': '#9333EA',
    'PINK': '#EC4899',
    'NAVY': '#1E3A8A',
    'GREY': '#6B7280',
    'GRAY': '#6B7280',
  };

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'newest', label: 'Newest' },
    { value: 'best-selling', label: 'Best Selling' },
    { value: 'price-asc', label: 'Price Low to High' },
    { value: 'price-desc', label: 'Price High to Low' },
    { value: 'name-asc', label: 'Alphabetically A-Z' },
    { value: 'name-desc', label: 'Alphabetically Z-A' },
  ];

  // Fetch dynamic filter data on component mount
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [variations, tags, priceData] = await Promise.all([
          productsApi.getBusinessVariations(),
          productsApi.getBusinessTags(),
          productsApi.getMaxMinPrices()
        ]);

        setBusinessVariations(variations || []);
        setBusinessTags(tags || []);
        if (priceData) {
          setPriceMinMax(priceData);
          setPriceRange([priceData.minPrice || 0, priceData.maxPrice || 10000]);
          setTempPriceRange([priceData.minPrice || 0, priceData.maxPrice || 10000]);
        }

        // Initialize expanded sections for each variation
        if (variations && variations.length > 0) {
          const newExpandedSections = {
            tags: false,
            price: false,
            availability: false,
          };
          variations.forEach(variation => {
            newExpandedSections[variation._id] = false;
          });
          setExpandedSections(newExpandedSections);
        }
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };

    fetchFilterData();
  }, []);

  // Load products when filters change
  useEffect(() => {
    loadProducts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortBy, selectedTags, priceRange, selectedVariations, categorySlug, subcategorySlug, searchTerm, team]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      // Only apply the price filter when the user has narrowed the slider —
      // sending the full default range hides products whose prices fall
      // outside the computed bounds
      const priceFilterActive =
        priceRange[0] > (priceMinMax.minPrice || 0) ||
        priceRange[1] < (priceMinMax.maxPrice || 10000);

      const response = await productsApi.getFilteredProducts({
        page: page,
        limit: itemsPerPage,
        search: searchTerm,
        categoryName: subcategorySlug || categorySlug || '',  // Prioritize subcategory over category
        minPrice: priceFilterActive ? Math.max(priceRange[0], 1) : 0,
        maxPrice: priceFilterActive ? priceRange[1] : 0,
        tags: selectedTags,
        variations: selectedVariations,
        sortBy: sortBy,
        team: team
      });

      if (response && response.data) {
        // Transform the data to match the ProductCard component expectations
        const transformedProducts = response.data.map(product => ({
          id: product.id || product._id,
          name: product.productName || product.name,
          price: product.discountedPrice || product.defaultPrice,
          originalPrice: product.normalPrice || product.defaultNormalPrice,
          imageUrl: product.image || product.featuredImage || '/api/placeholder/300/400',  // Changed from 'image' to 'imageUrl' to match ProductCard
          badge: product.badge,
          isNew: product.isNew,
          discount: product.discount,
          rating: product.rating || 4.5,
          reviews: product.reviews || 0,
          team: product.team,
          category: product.category,
          isPreOrder: product.isPreOrder
        }));

        setProducts(transformedProducts);
        setTotalProducts(response.count || 0);
      } else {
        setProducts([]);
        setTotalProducts(0);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  // Handler functions
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
    setPage(1);
  };

  const handleVariationToggle = (variationName, value) => {
    setSelectedVariations(prev => {
      const currentValues = prev[variationName] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];

      // If no values selected, remove the variation key
      if (newValues.length === 0) {
        const { [variationName]: _, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [variationName]: newValues
      };
    });
    setPage(1);
  };

  const handlePriceChange = (event, newValue) => {
    setTempPriceRange(newValue);
  };

  const handlePriceApply = () => {
    setPriceRange(tempPriceRange);
    setPage(1);
  };

  const handlePriceCancel = () => {
    setTempPriceRange(priceRange);
  };

  const handleClearAllFilters = () => {
    // Clear all filter states
    setSelectedTags([]);
    setPriceRange([priceMinMax.minPrice || 0, priceMinMax.maxPrice || 10000]);
    setTempPriceRange([priceMinMax.minPrice || 0, priceMinMax.maxPrice || 10000]);
    setSelectedVariations({});
    setSearchTerm('');
    setPage(1);
    setTeam('');

    // Clear URL query parameters
    navigate('/products', { replace: true });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const activeFiltersCount =
    selectedTags.length +
    Object.values(selectedVariations).reduce((acc, values) => acc + values.length, 0) +
    (priceRange[0] > (priceMinMax.minPrice || 0) || priceRange[1] < (priceMinMax.maxPrice || 10000) ? 1 : 0);

  // Filter Section Component
  const FilterSection = ({ title, name, children, count = 0 }) => (
    <Box sx={{ borderBottom: '1px solid #e5e5e5' }}>
      <Box
        onClick={() => toggleSection(name)}
        sx={{
          py: 2,
          px: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}
        >
          {title}
          {count > 0 && (
            <Box
              component="span"
              sx={{
                ml: 1,
                fontSize: '12px',
                color: '#666',
              }}
            >
              ({count})
            </Box>
          )}
        </Typography>
        {expandedSections[name] ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
      </Box>
      <Collapse in={expandedSections[name]}>
        <Box sx={{ pb: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );

  // Filter Sidebar Component
  const FilterSidebar = () => (
    <Box sx={{ width: { xs: '100%', md: '100%' } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 2, md: 3 },
        pb: { xs: 1.5, md: 2 },
        borderBottom: '1px solid #e5e5e5',
      }}>
        <Typography sx={{
          fontSize: { xs: '12px', sm: '13px', md: '14px' },
          fontWeight: 400,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}>
          Filters
        </Typography>
        {activeFiltersCount > 0 && (
          <Link
            component="button"
            onClick={handleClearAllFilters}
            sx={{
              fontSize: '12px',
              color: '#666',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: '#000',
              },
            }}
          >
            Clear All
          </Link>
        )}
      </Box>

      {/* Tags Filter */}
      {businessTags && businessTags.length > 0 && (
        <FilterSection title="Tags" name="tags" count={selectedTags.length}>
          <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
            {businessTags.map((tag) => {
              const tagName = tag.tagName || tag;
              return (
                <FormControlLabel
                  key={tagName}
                  control={
                    <Checkbox
                      checked={selectedTags.includes(tagName)}
                      onChange={() => handleTagToggle(tagName)}
                      size="small"
                      sx={{
                        color: '#666',
                        '&.Mui-checked': {
                          color: '#000',
                        },
                        padding: '4px 9px',
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '14px', color: '#333' }}>
                      {tagName}
                    </Typography>
                  }
                  sx={{
                    display: 'flex',
                    margin: 0,
                    py: 0.5,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                />
              );
            })}
          </Box>
        </FilterSection>
      )}

      {/* Price Range */}
      <FilterSection title="Price Range" name="price">
        <Box sx={{ px: 2 }}>
          <Typography sx={{ fontSize: '12px', color: '#666', mb: 1 }}>
            Minimum price is KES {priceMinMax.minPrice || 0}
          </Typography>
          <Slider
            value={tempPriceRange}
            onChange={handlePriceChange}
            valueLabelDisplay="auto"
            min={priceMinMax.minPrice || 0}
            max={priceMinMax.maxPrice || 10000}
            step={50}
            valueLabelFormat={(value) => `KSh ${value.toLocaleString()}`}
            sx={{
              color: '#000',
              '& .MuiSlider-thumb': {
                backgroundColor: '#fff',
                border: '2px solid #000',
                '&:hover': {
                  boxShadow: '0 0 0 8px rgba(0,0,0,0.08)',
                },
              },
              '& .MuiSlider-track': {
                backgroundColor: '#000',
                border: 0,
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#e5e5e5',
              },
            }}
          />
          <Typography sx={{ fontSize: '11px', color: '#999', mt: 1, mb: 2 }}>
            Range: KSh{tempPriceRange[0].toLocaleString()} - KSh{tempPriceRange[1].toLocaleString()}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <TextField
              size="small"
              value={tempPriceRange[0]}
              onChange={(e) => setTempPriceRange([Number(e.target.value), tempPriceRange[1]])}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '14px',
                  '& fieldset': {
                    borderColor: '#e5e5e5',
                  },
                  '&:hover fieldset': {
                    borderColor: '#666',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#000',
                    borderWidth: '1px',
                  },
                },
              }}
            />
            <TextField
              size="small"
              value={tempPriceRange[1]}
              onChange={(e) => setTempPriceRange([tempPriceRange[0], Number(e.target.value)])}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '14px',
                  '& fieldset': {
                    borderColor: '#e5e5e5',
                  },
                  '&:hover fieldset': {
                    borderColor: '#666',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#000',
                    borderWidth: '1px',
                  },
                },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handlePriceCancel}
              sx={{
                flex: 1,
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 400,
                py: 1,
                px: 2,
                borderColor: '#e5e5e5',
                color: '#666',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: '#f9f9f9',
                },
                '&:active': {
                  borderColor: '#000',
                  backgroundColor: '#f0f0f0',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePriceApply}
              sx={{
                flex: 1,
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                py: 1,
                px: 2,
                backgroundColor: '#000',
                color: '#fff',
                borderRadius: '4px',
                boxShadow: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#222',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                },
                '&:active': {
                  backgroundColor: '#111',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </FilterSection>

      {/* Dynamic Variation Filters */}
      {businessVariations && businessVariations.map((variation) => {
        const variationName = (variation?.variationName || '').toUpperCase();
        const selectedValues = selectedVariations[variationName] || [];

        // Color variation - special UI
        if (variationName === 'COLOR' || variationName === 'COLOUR' || variationName === 'COLOR ') {
          return (
            <FilterSection key={variation._id} title={variationName.trim()} name={variation._id} count={selectedValues.length}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, md: 1.5 } }}>
                {(variation.variationValues || []).map((colorValue) => {
                  const colorHex = colorHexMap[colorValue.toUpperCase()] || '#808080';
                  return (
                    <Box
                      key={colorValue}
                      onClick={() => handleVariationToggle(variationName, colorValue)}
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: '24px', sm: '28px', md: '32px' },
                          height: { xs: '24px', sm: '28px', md: '32px' },
                          borderRadius: '50%',
                          backgroundColor: colorHex,
                          border: colorValue.toUpperCase() === 'WHITE' ? '1px solid #e5e5e5' : 'none',
                          position: 'relative',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        {selectedValues.includes(colorValue) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              backgroundColor: colorValue.toUpperCase() === 'BLACK' || colorValue.toUpperCase() === 'NAVY' ? '#fff' : '#000',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </FilterSection>
          );
        }

        // Size variation - button UI
        if (variationName === 'SIZE') {
          return (
            <FilterSection key={variation._id} title={variationName} name={variation._id} count={selectedValues.length}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(variation.variationValues || []).map((size) => (
                  <Box
                    key={size}
                    onClick={() => handleVariationToggle(variationName, size)}
                    sx={{
                      minWidth: size.length > 5 ? '80px' : '48px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: selectedValues.includes(size) ? '2px solid #000' : '1px solid #e5e5e5',
                      backgroundColor: selectedValues.includes(size) ? '#000' : '#fff',
                      color: selectedValues.includes(size) ? '#fff' : '#333',
                      fontSize: '11px',
                      cursor: 'pointer',
                      padding: '0 8px',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#000',
                      },
                    }}
                  >
                    {size}
                  </Box>
                ))}
              </Box>
            </FilterSection>
          );
        }

        // Default checkbox UI for other variations
        return (
          <FilterSection key={variation._id} title={variationName} name={variation._id} count={selectedValues.length}>
            {(variation.variationValues || []).map((value) => (
              <FormControlLabel
                key={value}
                control={
                  <Checkbox
                    checked={selectedValues.includes(value)}
                    onChange={() => handleVariationToggle(variationName, value)}
                    size="small"
                    sx={{
                      color: '#666',
                      '&.Mui-checked': {
                        color: '#000',
                      },
                      padding: '4px 9px',
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px', color: '#333' }}>
                    {value}
                  </Typography>
                }
                sx={{
                  display: 'flex',
                  margin: 0,
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            ))}
          </FilterSection>
        );
      })}
    </Box>
  );

  const totalPages = Math.ceil(totalProducts / itemsPerPage);
  const displayTitle = searchQuery
    ? `Search results for "${searchQuery}"`
    : (subcategorySlug || categorySlug || 'All Products');

  return (
    <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3, lg: 4 } }}>
      <Seo title="Products" description="Browse our full product catalog." />
      {/* Title and Product Count */}
      <Box sx={{
        mt: { xs: 2, sm: 3, md: 4 },
        mb: { xs: 3, sm: 4, md: 5 },
        textAlign: 'left',
      }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: '24px', sm: '28px', md: '32px', lg: '36px' },
            fontWeight: 300,
            letterSpacing: '0.02em',
            mb: { xs: 0.5, sm: 1 },
            lineHeight: { xs: 1.2, sm: 1.3 },
          }}
        >
          {displayTitle}
        </Typography>
        <Typography sx={{
          fontSize: { xs: '12px', sm: '13px', md: '14px' },
          color: '#666',
          letterSpacing: '0.05em',
        }}>
          {totalProducts} {totalProducts === 1 ? 'Product' : 'Products'}
        </Typography>
      </Box>

      {/* Filters and Sort Bar */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        mb: { xs: 3, sm: 4 },
        pb: { xs: 1.5, sm: 2 },
        borderBottom: '1px solid #f0f0f0',
      }}>
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 2 },
          alignItems: 'center',
          order: { xs: 2, sm: 1 },
        }}>
          {/* Mobile Filters Button */}
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setMobileFiltersOpen(true)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              textTransform: 'none',
              color: '#000',
            }}
          >
            Filters
            {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
          </Button>

          {/* Desktop Filter Toggle */}
          <Button
            startIcon={<ViewSidebarIcon />}
            onClick={() => setDesktopFiltersOpen(!desktopFiltersOpen)}
            sx={{
              display: { xs: 'none', md: 'flex' },
              textTransform: 'none',
              color: desktopFiltersOpen ? 'var(--primary)' : '#000',
              border: desktopFiltersOpen ? '1px solid var(--primary)' : '1px solid #e0e0e0',
            }}
          >
            {desktopFiltersOpen ? 'Hide Filters' : 'View Filters'}
            {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
          </Button>

          {/* Active Filter Chips */}
          {activeFiltersCount > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  onDelete={() => handleTagToggle(tag)}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Sort Dropdown */}
        <FormControl size="small" sx={{
          minWidth: { xs: '100%', sm: 180, md: 200 },
          order: { xs: 1, sm: 2 },
        }}>
          <Select
            value={sortBy}
            onChange={handleSortChange}
            displayEmpty
            sx={{ fontSize: { xs: '12px', sm: '13px', md: '14px' } }}
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Main Content */}
      <Grid container spacing={{ xs: 2, sm: 3 }} direction="row">
        {/* Desktop Filters Sidebar */}
        {desktopFiltersOpen && (
          <Grid item md={2.4} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'sticky', top: { md: 60, lg: 80 } }}>
              <FilterSidebar />
            </Box>
          </Grid>
        )}

        {/* Products Grid */}
        <Grid item xs={12} md={desktopFiltersOpen ? 9.6 : 12}>
          <Grid container spacing={{ xs: 1, sm: 1.5, md: 2, lg: 2.5 }}>
            {loading
              ? Array.from({ length: itemsPerPage }).map((_, index) => (
                <Grid
                  item xs={6} sm={4}
                  md={gridItemWidth(desktopFiltersOpen ? gridColumns - 1 : gridColumns)}
                  lg={gridItemWidth(desktopFiltersOpen ? gridColumns : gridColumns + 1)}
                  xl={gridItemWidth(desktopFiltersOpen ? gridColumns + 1 : gridColumns + 2)}
                  key={index}
                >
                  <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 0 }} />
                </Grid>
              ))
              : products.map((product) => (
                <Grid
                  item xs={6} sm={4}
                  md={gridItemWidth(desktopFiltersOpen ? gridColumns - 1 : gridColumns)}
                  lg={gridItemWidth(desktopFiltersOpen ? gridColumns : gridColumns + 1)}
                  xl={gridItemWidth(desktopFiltersOpen ? gridColumns + 1 : gridColumns + 2)}
                  key={product.id}
                >
                  <ProductCard product={product} />
                </Grid>
              ))}
          </Grid>

          {/* No Products Found */}
          {products.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: { xs: 8, sm: 10, md: 12 } }}>
              <Typography sx={{
                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                color: '#666',
                mb: { xs: 1.5, sm: 2 },
                px: { xs: 2, sm: 0 }
              }}>
                No products found matching your criteria
              </Typography>
              <Button
                variant="outlined"
                onClick={handleClearAllFilters}
                sx={{
                  textTransform: 'none',
                  fontSize: { xs: '12px', sm: '13px', md: '14px' },
                  borderColor: '#000',
                  color: '#000',
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  '&:hover': {
                    borderColor: '#000',
                    backgroundColor: '#000',
                    color: '#fff',
                  },
                }}
              >
                Clear All Filters
              </Button>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: { xs: 4, sm: 6, md: 8 },
              mb: { xs: 2, sm: 3, md: 4 }
            }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                size="medium"
                variant="outlined"
                shape="rounded"
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Mobile Filters Drawer */}
      <Drawer
        anchor="left"
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        <Box sx={{ width: { xs: '85vw', sm: '320px' }, maxWidth: '320px' }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid #e5e5e5',
          }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>Filters</Typography>
            <IconButton onClick={() => setMobileFiltersOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 2, overflowY: 'auto', maxHeight: 'calc(100vh - 140px)' }}>
            <FilterSidebar />
          </Box>
          <Box sx={{ p: 2, borderTop: '1px solid #e5e5e5' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setMobileFiltersOpen(false)}
              sx={{ backgroundColor: '#000', textTransform: 'none' }}
            >
              View {products.length} Products
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Container>
  );
};

export default ProductsWithDynamicFilters;